import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Link } from "react-router";
import Layout from "../components/Layout";
import { BASE_API_URL } from "../utils/constants";
import toLocalTime from "../utils/toLocalTime";
import { BaseLivlyApiResponse } from "../types/Base";
import { Spinner } from "../components/Spinner";
import useLivlyUser from "../context/UserProvider";
import { ServiceTypeEnum } from "../types/Building";
import { useServiceDisplayName } from "../hooks/useServiceDisplayName";

export type Event = {
  eventId: string;
  propertyId: number;
  propertyName: string;
  timezone: string;
  title: string;
  location: string;
  imageUrl: string;
  startDate: string | Date;
  endDate: string | Date;
  attending: boolean;
  isCancelled: boolean;
  rsvpDeadline: string | Date | null;
};

const getEvents = async (propertyId: number) => {
  const { data } = await axios.get<BaseLivlyApiResponse<Event[]>>(
    `${BASE_API_URL}/resident/events/property/${propertyId}/me/upcoming?includeCancelled=false`
  );

  return data.Data;
};

export const eventsQuery = (propertyId: number, leaseId: number) => ({
  queryKey: ["events", propertyId, leaseId],
  queryFn: async () => getEvents(propertyId),
});

export default function EventsPage() {
  const { user } = useLivlyUser();

  const { data, isLoading } = useQuery({
    ...eventsQuery(user.propertyId, user.propertyUnitLeaseId),
  });

  const eventsDisplayName = useServiceDisplayName(
    user.building?.serviceTypes,
    ServiceTypeEnum.Events,
    "Events"
  );

  return (
    <Layout title={eventsDisplayName}>
      {isLoading ? (
        <div className="flex items-center justify-center flex-1 my-56">
          <Spinner color="livly" size="xl" />
        </div>
      ) : (
        <>
          <h2 className="mb-4">
            Browse available events and submit a booking request.
          </h2>
          <div className="grid lg:grid-cols-3 lg:gap-4 auto-rows-max">
            {data?.map((livlyEvent) => {
              return (
                <div
                  key={livlyEvent.eventId}
                  className="h-auto"
                >
                  <Link
                    to={`/lease/${user.propertyUnitLeaseId}/events/property/${user.propertyId}/${livlyEvent.eventId}`}
                    className="flex flex-col h-full overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md"
                  >
                    {livlyEvent.imageUrl ? (
                      <img
                        className="object-cover w-full h-48"
                        src={livlyEvent.imageUrl}
                        alt={livlyEvent.title}
                      />
                    ) : (
                      <div className="flex items-center justify-center w-full h-48 bg-gray-50">
                        <FontAwesomeIcon icon="dumbbell" size="2x" />
                      </div>
                    )}
                    <div className="flex flex-col flex-1 p-4">
                      <p className="mb-1 text-sm font-light">
                        {toLocalTime(
                          livlyEvent.startDate,
                          livlyEvent.timezone
                        ).format("dddd, MMMM D, YYYY")}
                      </p>
                      <p className="font-bold">{livlyEvent.title}</p>
                      <p className="text-sm text-gray-500">
                        {livlyEvent.location}
                      </p>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Layout>
  );
}
